/* eslint-disable import/no-named-as-default-member */
/* eslint-disable @typescript-eslint/no-explicit-any */
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import * as Yup from 'yup'

import { commonSchema, validateAgeRange } from './common'
import { ALL_TSHIRT_SIZES, TSHIRT_SIZES } from '../../../constants/models'
import { isCategoryMultisports } from '../../../utils/ApiHelpers'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.tz.setDefault('Asia/Manila')
dayjs.extend(customParseFormat)

const validateTandemField = (value: any, testContext: any) => {
  if (testContext.parent.isTandem)
    if (value !== undefined && value !== null) {
      return value && value.toString().trim().length > 0
    } else return false

  return true
}

const validateRequiredConfirmPhone = (value: any, testContext: any) => {
  if (!testContext.parent.isOnsiteRegistration) {
    if (value !== undefined && value !== null)
      return value && value.toString().trim().length > 0
    return false
  }

  return true
}

const validateMatchConfirmPhone = (
  value: any,
  testContext: any,
  matchTo: string,
) => {
  if (!testContext.parent.isOnsiteRegistration) {
    if (value !== undefined && value !== null)
      return value && value.toString().trim().length > 0 && value === matchTo
    return false
  }

  return true
}

const validateJerseyField = (value: any, testContext: any) => {
  if (
    testContext.parent.hasJersey &&
    testContext.parent.relayEvent.includes('BIKE')
  )
    if (value !== undefined && value !== null) {
      return value && value.toString().trim().length > 0
    } else return false

  return true
}

const ParticipantSchema = Yup.object().shape({
  ...commonSchema,

  // ONSITE REGISTRATION
  isOnsiteRegistration: Yup.boolean(),
  confirmPhoneNumber: Yup.string()
    .max(20, 'Confirm Phone must not exceed 20 characters')
    .test(
      'required-when-not-onsite-registration',
      'Confirm Phone is required',
      validateRequiredConfirmPhone,
    )
    .test(
      'match-to-phoneNumber',
      'Phone numbers must match',
      (value, testContext) => {
        return validateMatchConfirmPhone(
          value,
          testContext,
          testContext.parent.phoneNumber,
        )
      },
    ),
  email: Yup.string().email('Email Address must be a valid email'),

  confirmEmergencyContactPhone: Yup.string()
    .max(20, 'Confirm Emergency Contact Number must not exceed 20 characters')
    .test(
      'required-when-not-onsite-registration',
      'Confirm Emergency Contact Number is required',
      validateRequiredConfirmPhone,
    )
    .test(
      'match-to-emergencyContactPhone',
      'Emergency Contact Phone numbers must match',
      (value, testContext) => {
        return validateMatchConfirmPhone(
          value,
          testContext,
          testContext.parent.emergencyContactPhone,
        )
      },
    ),
  // .required('Confirm Emergency Contact Number is required')
  // .oneOf(
  //   [Yup.ref('emergencyContactPhone'), null],
  //   'Emergency Contact Phone numbers must match',
  // ),

  // TRIATHLON/DUATHLON relay categories
  isTriathlon: Yup.boolean(),
  teamType: Yup.string(),
  relayEvent: Yup.array()
    .of(Yup.string())
    .test(
      'required-with-triathlon',
      'Relay Event is required',
      (value, testContext) => {
        if (isCategoryMultisports(testContext.parent))
          return Array.isArray(value) && value.length > 0
        return true
      },
    ),
  // TANDEM categories
  isTandem: Yup.boolean(),
  tandemAgeRange: Yup.mixed(),
  tandemName: Yup.string()
    .test(
      'required-with-tandem',
      'Tandem participant name is required',
      validateTandemField,
    )
    .test(
      'validate',
      'Tandem participant name must not exceed 50 characters',
      (value) => {
        if (value !== undefined && value !== null)
          return /^[a-z ,.'-]{1,50}$/i.test(value.toString())
        return true
      },
    ),
  tandemBirthdate: Yup.mixed()
    .test(
      'required-with-tandem',
      'Tandem participant birthdate is required',
      validateTandemField,
    )
    .test(
      'validate-format',
      'Tandem participant birthdate must be valid date in format YYYY-MM-DD',
      (value) => {
        if (value !== undefined && value !== null)
          return dayjs(value as string, 'YYYY-MM-DD', true).isValid()
        return true
      },
    )
    .test({
      name: 'validate-range',
      test: (value: any, testContext: any) =>
        validateAgeRange(value, testContext, 'tandemAgeRange'),
    })
    .test(
      'validate-future',
      'Child birthdate must not be a future date',
      (value) => {
        if (value !== undefined && value !== null)
          return dayjs().isAfter(value as string)
        return true
      },
    ),
  tandemShirtSize: Yup.mixed()
    .test(
      'required-with-tandem',
      'Tandem participant t-shirt size is required',
      validateTandemField,
    )
    .oneOf(Object.keys(TSHIRT_SIZES)),
  // Additional ID
  additionalId: Yup.string(),
  // firstParticipants
  firstParticipants: Yup.mixed().nullable(),
  firstParticipantInfo: Yup.string().test({
    name: 'validate-length',
    test: (value: any, testContext: any) => {
      // console.log({ value, testContext })
      if (value !== undefined && value !== null) {
        if ('firstParticipants' in testContext.parent) {
          const length =
            testContext.parent.firstParticipants.INFO_LABEL_LENGTH ?? 25
          if (testContext.parent.firstParticipantInfo.trim().length > length) {
            return testContext.createError({
              message: `Must not exceed ${length} characters`,
              path: testContext.path, // Fieldname
            })
          }
        }
      }
      return true
    },
  }),
  // subCategories
  subCategories: Yup.mixed().nullable(),
  subCategory: Yup.string().test({
    name: 'validate-length',
    test: (value: any, testContext: any) => {
      // console.log({ value, testContext })
      if (
        'subCategories' in testContext.parent &&
        Object.keys(testContext.parent.subCategories || {}).length > 0
      ) {
        if (
          'required' in testContext.parent.subCategories &&
          testContext.parent.subCategories.required
        ) {
          if ((value || '').length === 0) {
            return testContext.createError({
              message: `${testContext.parent.subCategories.label} is required`,
              path: testContext.path, // Fieldname
            })
          }
        }
        if (value !== undefined && value !== null) {
          const subCategoryValues =
            testContext.parent.subCategories.options.map((a) => a.name)
          if (!subCategoryValues.includes(value)) {
            return testContext.createError({
              message: `Invalid ${testContext.parent.subCategories.label} selected`,
              path: testContext.path, // Fieldname
            })
          }
        }
      }
      return true
    },
  }),
  jerseySize: Yup.mixed()
    .test('required-with-hasJersey', 'Jersey is required', validateJerseyField)
    .oneOf(Object.keys(ALL_TSHIRT_SIZES)),
})

export default ParticipantSchema
